import React, { FC, useMemo, useRef, useState } from 'react'

import Editor from '@draft-js-plugins/editor'
import createMentionPlugin, { defaultSuggestionsFilter } from '@draft-js-plugins/mention'
import { MentionData } from '@draft-js-plugins/mention'
import { DraftBlockType, EditorState, RichUtils } from 'draft-js'
import 'draft-js/dist/Draft.css'

import mentionsStyle from '../../../styles/mentionsStyle.module.css'
import {
  TEXT_EDITOR_BLOCK_TYPES,
  TEXT_EDITOR_INLINE_STYLES,
  TEXT_EDITOR_STYLE_MAP,
} from '@/constants/textEditor'
import { useGetStaffUsersQuery } from '@/services/user'

function getBlockStyle(block: any) {
  switch (block.getType()) {
    case 'blockquote':
      return 'text-editor-blockquote'
    default:
      return null
  }
}

const StyleButton: FC<any> = ({ onToggle, style, active, label }) => {
  let className = 'text-gray-600 cursor-pointer inline-block p-[2px] mr-2 hover:text-slate-400'

  if (active) {
    className = ' cursor-pointer inline-block p-[2px] mr-2 bg-slate-400 text-white'
  }

  return (
    <span
      className={className}
      onMouseDown={onToggle}
    >
      {label}
    </span>
  )
}

const BlockStyleControls: FC<any> = (props) => {
  const { editorState, onToggle } = props
  const selection = editorState.getSelection()
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType()
  return (
    <span className='text-[14px] mb-1 inline-block'>
      {TEXT_EDITOR_BLOCK_TYPES.map(({ label, style }) => (
        <StyleButton
          key={label}
          active={style === blockType}
          label={label}
          onToggle={() => onToggle(style)}
          style={style}
        />
      ))}
    </span>
  )
}

const InlineStyleControls: FC<any> = ({ onToggle, editorState }) => {
  const currentStyle = editorState.getCurrentInlineStyle()

  return (
    <span className='text-[14px] ml-2 border-l pl-2 inline-block'>
      {TEXT_EDITOR_INLINE_STYLES.map(({ label, style }) => (
        <StyleButton
          key={label}
          active={currentStyle.has(style)}
          label={label}
          onToggle={() => onToggle(style)}
          style={style}
        />
      ))}
    </span>
  )
}

interface CustomTextEditorMentionProps {
  onChange: (editorName: string, editorState: EditorState) => void
  onMention: (user: any) => void
  editorName: string
  editorState: EditorState
  placeholder?: string
}

export const CustomTextEditorMention: FC<CustomTextEditorMentionProps> = ({
  onChange,
  onMention,
  editorName,
  editorState,
  placeholder = 'Enter text',
}) => {
  const { data: users } = useGetStaffUsersQuery()
  const [open, setOpen] = useState(false)
  const editor = useRef<Editor>(null)

  const handleChange = (editorState: EditorState) => {
    onChange(editorName, editorState)
  }

  const focus = () => editor.current?.focus()

  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command)

    if (newState) {
      handleChange(newState)
      return 'handled'
    }

    return 'not-handled'
  }

  const userList = useMemo<MentionData[]>((): MentionData[] => {
    const list: any =
      users &&
      users.map((user) => {
        return {
          id: user.data?.id,
          name:
            user.first_name && user.last_name
              ? user.first_name + ' ' + user.last_name
              : user.data?.full_name || user.first_name || user.last_name || user.email,
          user: user,
        }
      })

    return list || []
  }, [users])

  const [suggestions, setSuggestions] = useState<MentionData[]>(userList)

  const contentState = editorState.getCurrentContent()
  let className = 'text-[14px]'

  if (!contentState.hasText()) {
    if (contentState.getBlockMap().first().getType() !== 'unstyled') {
      className += ' RichEditor-hidePlaceholder'
    }
  }

  const [{ MentionSuggestions, plugins }] = useState(() => {
    const mentionPlugin = createMentionPlugin({
      theme: mentionsStyle,
      mentionPrefix: '@',
      supportWhitespace: true,
    })

    const { MentionSuggestions } = mentionPlugin

    const plugins = [mentionPlugin]
    return {
      plugins,
      MentionSuggestions,
    }
  })

  const onSearchChange = ({ value }: { value: string }) => {
    setSuggestions(defaultSuggestionsFilter(value, userList))
  }

  const onOpenChange = (_open: boolean) => {
    setOpen(_open)
  }

  const toggleBlockType = (blockType: DraftBlockType) => {
    handleChange(RichUtils.toggleBlockType(editorState, blockType))
  }
  const toggleInlineStyle = (inlineStyle: string) => {
    handleChange(RichUtils.toggleInlineStyle(editorState, inlineStyle))
  }

  return (
    <div className='bg-white border p-3 mb-2 min-h-[160px]'>
      <div className='overflow-x-auto mb-2 p-1 border-b whitespace-nowrap'>
        <BlockStyleControls
          editorState={editorState}
          onToggle={toggleBlockType}
        />
        <InlineStyleControls
          editorState={editorState}
          onToggle={toggleInlineStyle}
        />
      </div>
      <div
        className={className}
        onClick={focus}
      >
        <Editor
          blockStyleFn={getBlockStyle}
          customStyleMap={TEXT_EDITOR_STYLE_MAP}
          placeholder={placeholder}
          editorKey={'editor'}
          editorState={editorState}
          handleKeyCommand={handleKeyCommand}
          onChange={handleChange}
          plugins={plugins}
          ref={editor}
          spellCheck={true}
        />
        <MentionSuggestions
          open={open}
          onOpenChange={onOpenChange}
          suggestions={suggestions}
          onSearchChange={onSearchChange}
          onAddMention={onMention}
        />
      </div>
    </div>
  )
}

export default CustomTextEditorMention
